/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { useEffect, useRef, useState, useCallback } from 'react'
import dayjs from 'dayjs'
import colors from '@sportninja/common/constants/appColors'
import { connect } from 'react-redux'
import { Spin, FloatButton } from 'antd'
import { ArrowUpOutlined } from '@ant-design/icons'
import {
  getGameStatusTypesBySportId,
  getAllGameTypes,
} from '@sportninja/common/selectors/types'
import req from '@sportninja/common/api/request'

import { Flex } from '../../Layout'
import DropdownArrowIcon from '../../icons/DropdownArrowIcon'
import { media, Mobile } from '../../Responsive'
import { paragraphSmall, weightRegular } from '../../css'
import ListStatusView from '../StatusView'
import useGetScheduleFiltering from '../useGetScheduleFiltering'
import GameListRow from './GameListRow'
import GameListFilters from './GameListFilters'
import MobileGameListRow from './MobileGameListRow'
import { useUrlManager } from '../../../hooks/useUrlManager'
import Text from 'src/components/ts-components/Text'

const SCROLL_THRESHOLD = 500

export const DayOfGames = ({
  date,
  games = [],
  gameStatusTypes,
  gameTypes,
  isMobile,
  isSoccer = false,
  isHockey = false,
  onClickFirstGame = () => {},
  scheduleId = null,
  isMySchedule = false,
}) => {
  const [isOpen, setIsOpen] = useState(true)

  return (
    <div
      css={css`
        ${isOpen ? 'margin-bottom: 16px;' : ''}

        ${media.mobile} {
          padding: 0 8px;
        }
      `}
    >
      <Flex
        onClick={() => {
          setIsOpen(!isOpen)
        }}
        alignItems='center'
        justifyContent='space-between'
        className='day-of-games'
        css={css`
          height: 40px;
          border-radius: 8px;
          background-color: ${colors.SECONDARY_300};
          padding: 8px 16px;
          margin-bottom: 16px;
          cursor: pointer;
          transition: filter 0.2s ease-in-out;
          &:hover {
            filter: brightness(1.2);
          }
          svg {
            transition: transform 0.1s ease-in-out;
            ${!isOpen && 'transform: rotate(180deg);'}
          }
        `}
      >
        <Text variant='captionSmall' color={colors.NEUTRAL_0} weight='semiBold'>
          {date}
        </Text>
        <DropdownArrowIcon color={colors.NEUTRAL_0} />
      </Flex>
      {isOpen &&
        games.map((game, idx) => {
          if (isMobile) {
            return (
              <MobileGameListRow
                key={game.id}
                hasNextSibling={games.length > 1 && idx !== games.length - 1}
                game={game}
                gameStatusTypes={gameStatusTypes}
                gameTypes={gameTypes}
                isSoccer={isSoccer}
                isHockey={isHockey}
                scheduleId={scheduleId}
                isMySchedule={isMySchedule}
              />
            )
          }

          return (
            <GameListRow
              key={game.id}
              hasNextSibling={games.length > 1 && idx !== games.length - 1}
              game={game}
              gameStatusTypes={gameStatusTypes}
              gameTypes={gameTypes}
              isSoccer={isSoccer}
              isHockey={isHockey}
              isFirstGame={idx === 0}
              isLastGame={idx === games?.length - 1}
              onClickFirstGame={onClickFirstGame}
              scheduleId={scheduleId}
              isMySchedule={isMySchedule}
            />
          )
        })}
    </div>
  )
}

const GameList = ({
  team_id,
  schedule_id,
  gameStatusTypes,
  gameTypes,
  slug: _slug = '/games',
  refreshKey,
  setVisibleGameIds,
  areFiltersHidden,
  setFilterForExporting = () => {},
  isSoccer = false,
  isHockey = false,
  onClickFirstGame = () => {},
  // this is only for team filtering
  scheduleId = null,
}) => {
  const {
    currentParams,
    teams,
    team,
    setTeam,
    filters,
    dateRange,
    setDateRange,
    state,
    setState,
    sort,
    setSort,
    loaded,
    // Venue/Facility filter values
    venues,
    venue,
    setVenue,
    facility,
    setFacility,
    loadingVenues,
  } = useGetScheduleFiltering(
    {
      team_id,
      schedule_id,
    },
    {
      autoSelectMostRecentComp: true,
      onScheduleChange: (id) => {
        setSlug(`/schedules/${id}/games`)
      },
    }
  )

  const [error, setError] = useState(false)
  const [loading, setLoading] = useState(true)
  const [items, setItems] = useState([])
  const [pagination, setPagination] = useState({})
  const [isAppending, setIsAppending] = useState(false)
  const isAppendingRef = useRef(false)
  const itemsRef = useRef([])

  useEffect(() => {
    itemsRef.current = items
  }, [items])

  useEffect(() => {
    console.log('🔄 GameList: MOUNTED')
    return () => console.log('🔄 GameList: UNMOUNTED')
  }, [])

  const [slug, setSlug] = useState(
    currentParams?.sn_division
      ? `/schedules/${currentParams.sn_division}/games`
      : currentParams?.sn_schedule
      ? `/schedules/${currentParams.sn_schedule}/games`
      : _slug
  )
  const { setParam } = useUrlManager()

  const lastDateRange = useRef({
    starts_at: undefined,
    ends_at: undefined,
  })

  const getItems = useCallback(
    async (page = 1, append = false) => {
      try {
        if (append) {
          setIsAppending(true)
          isAppendingRef.current = true
        } else {
          setLoading(true)
        }

        const query = { page, order: sort, exclude_cancelled_games: 1 }
        if (team_id || team) {
          query.team_id = team_id || team
        }
        // Add venue/facility filters
        if (venue) {
          query.venue_id = venue
        }
        if (facility) {
          query.facility_id = facility
        }
        if (dateRange.starts_at && dateRange.ends_at) {
          query.starts_after = dayjs(dateRange.starts_at)
            .hour(0)
            .minute(0)
            .second(0)
            .format()

          query.starts_before = dayjs(dateRange.ends_at)
            .hour(0)
            .minute(0)
            .second(0)
            .add(1, 'day')
            .format()
        } else if (!currentParams.sn_sort) {
          query.default = 1
        }

        if (currentParams.sn_sort) {
          query.order = currentParams.sn_sort
        }

        // Only update filter for exporting on initial load, not during append
        // This prevents unnecessary parent state updates that cause scroll reset
        if (!append) {
          setFilterForExporting(query)
        }
        const response = await req(slug, { query })

        if (append) {
          setItems((prevItems) => {
            const newItems = response.data.filter(
              (newItem) =>
                !prevItems.some((prevItem) => prevItem.id === newItem.id)
            )
            return [...prevItems, ...newItems]
          })
        } else {
          setItems(response.data)
        }

        if (typeof setVisibleGameIds === 'function') {
          if (append) {
            setVisibleGameIds((prevIds) => {
              const newIds = response.data
                .map((d) => d.id)
                .filter((id) => !prevIds.includes(id))
              return [...prevIds, ...newIds]
            })
          } else {
            setVisibleGameIds(response.data.map((d) => d.id))
          }
        }
        setPagination(response.meta.pagination)
      } catch (e) {
        setError(e?.message)
      } finally {
        setLoading(false)
        setIsAppending(false)
        isAppendingRef.current = false
      }
    },
    [
      sort,
      team_id,
      team,
      dateRange,
      currentParams,
      slug,
      setFilterForExporting,
      setVisibleGameIds,
      venue,
      facility,
    ]
  )

  const hasMore = pagination && pagination.current_page < pagination.total_pages

  const loadMoreGames = useCallback(() => {
    if (loading || isAppendingRef.current || !hasMore) {
      return
    }
    const nextPage = (pagination.current_page || 0) + 1
    getItems(nextPage, true)
  }, [loading, hasMore, pagination.current_page, getItems])

  const handleScroll = useCallback(() => {
    const { scrollTop, scrollHeight, clientHeight } = document.documentElement
    const nearBottom =
      scrollHeight - (scrollTop + clientHeight) < SCROLL_THRESHOLD
    if (nearBottom && hasMore && !loading && !isAppendingRef.current) {
      loadMoreGames()
    }
  }, [hasMore, loading, loadMoreGames])

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [handleScroll])

  useEffect(() => {
    const lastDateRangeWasSet =
      Object.values(lastDateRange.current).filter(
        (v) => typeof v !== 'undefined'
      ).length === 2
    if (dateRange.starts_at && dateRange.ends_at) {
      lastDateRange.current = dateRange
      getItems(1, false)
    } else if (
      lastDateRangeWasSet &&
      !dateRange.starts_at &&
      !dateRange.ends_at
    ) {
      getItems(1, false)
    }
  }, [dateRange.starts_at, dateRange.ends_at])

  useEffect(() => {
    if (loaded) {
      getItems(1, false)
    }
  }, [loaded, sort, team, slug, refreshKey, venue, facility])

  let dates = {}
  items.forEach((item) => {
    const day = dayjs(item.starts_at).format('dddd, MMM D, YYYY')
    dates[day] = dates[day] || []
    dates[day].push(item)
  })

  const today = dayjs()
  const isTodayEnabled =
    dateRange.starts_at &&
    dateRange.ends_at &&
    today.isSame(dateRange.starts_at, 'day') &&
    today.isSame(dateRange.ends_at, 'day')

  return (
    <div
      css={css`
        margin-top: 24px;
        margin-bottom: 84px;
      `}
    >
      <GameListFilters
        filters={filters}
        dateRange={dateRange}
        team_id={team_id}
        onTeamChange={setTeam}
        onScheduleChange={(newScheduleId) => {
          if (newScheduleId) {
            setSlug(`/schedules/${newScheduleId}/games`)
          } else {
            setSlug(_slug)
          }
        }}
        setDateRange={setDateRange}
        state={state}
        setState={setState}
        sort={sort}
        setSort={(value) => {
          if (isTodayEnabled) {
            setDateRange({
              starts_at: undefined,
              ends_at: undefined,
            })
          }
          setSort(value)
        }}
        teams={teams}
        team={team}
        loading={loading}
        useDateRange
        useGameOrder
        isHidden={areFiltersHidden}
        // Venue/Facility filter props
        venues={venues}
        venue={venue}
        onVenueChange={setVenue}
        facility={facility}
        onFacilityChange={setFacility}
        loadingVenues={loadingVenues}
        useVenueFilter
      >
        <Flex
          column
          noFlex
          css={css`
            ${media.mobile} {
              align-self: flex-start;
            }
          `}
        >
          <Flex
            alignItems='flex-end'
            css={css`
              min-width: 170px;
            `}
          >
            <Flex
              as='button'
              type='button'
              alignItems='center'
              id='game-list-todays-game'
              className={isTodayEnabled ? 'active' : ''}
              css={css`
                ${paragraphSmall}
                ${weightRegular}
                height: 40px;
                transition: background-color 0.2s ease-in-out;
                border: 1px solid #3f4753;
                border-radius: 8px;
                padding: 0 16px;
                color: white;

                &:hover {
                  border-color: white;
                }
              `}
              onClick={() => {
                if (isTodayEnabled) {
                  setParam('sn_today', '')
                  setParam('sn_starts_at', '')
                  setParam('sn_ends_at', '')
                  setDateRange({
                    starts_at: undefined,
                    ends_at: undefined,
                  })
                } else {
                  const todayStart = dayjs().hour(0).minute(0).second(0)
                  const tilTomorrow = todayStart
                  setParam('sn_today', 'true')
                  setParam(
                    'sn_starts_at',
                    dayjs(todayStart).format('YYYY-MM-DD')
                  )
                  setParam(
                    'sn_ends_at',
                    dayjs(tilTomorrow).format('YYYY-MM-DD')
                  )
                  setDateRange({
                    starts_at: todayStart,
                    ends_at: tilTomorrow,
                  })
                }
              }}
            >
              Today&apos;s Games
              <Flex
                id='game-list-todays-game-toggle'
                className={isTodayEnabled ? 'active' : ''}
                alignItems='center'
                css={css`
                  position: relative;
                  height: 12px;
                  width: 22px;
                  transition: background-color 0.1s, border 0.1s ease-in-out;
                  ${isTodayEnabled &&
                  `background-color: ${colors.DEFAULT_FLAIR};`}
                  border: 2px solid white;
                  border-radius: 6px;
                  margin-left: 16px;

                  &.active {
                    border-color: ${colors.DEFAULT_FLAIR};
                  }
                `}
              >
                <div
                  id='game-list-todays-game-toggle-pip'
                  className={isTodayEnabled ? 'active' : ''}
                  css={css`
                    background-color: white;
                    height: 6px;
                    width: 6px;
                    border-radius: 50%;
                    transition: transform 0.1s,
                      background-color 0.1s ease-in-out;
                    transform: translateX(${isTodayEnabled ? 9 : 1}px);

                    $.active {
                      background-color: ${colors.DEFAULT_FLAIR};
                    }
                  `}
                />
              </Flex>
            </Flex>
          </Flex>
        </Flex>
      </GameListFilters>
      {loading || items.length === 0 ? (
        <ListStatusView
          loading={loading}
          zeroItems={items.length === 0}
          noItemsText='games'
          error={error}
        />
      ) : (
        <>
          <Mobile>
            {(isMobile) => {
              return Object.keys(dates).map((date) => {
                const listOfGames = dates[date]
                return (
                  <DayOfGames
                    key={date}
                    date={date}
                    games={listOfGames}
                    gameStatusTypes={gameStatusTypes}
                    gameTypes={gameTypes}
                    isMobile={isMobile}
                    isSoccer={isSoccer}
                    isHockey={isHockey}
                    onClickFirstGame={onClickFirstGame}
                    scheduleId={schedule_id || scheduleId}
                  />
                )
              })
            }}
          </Mobile>
          <div
            css={css`
              display: flex;
              justify-content: center;
              align-items: center;
              padding: 24px 0;
              min-height: 60px;
            `}
          >
            {isAppending && (
              <div
                css={css`
                  display: inline-flex;
                  align-items: center;
                  justify-content: center;
                  gap: 8px;
                `}
              >
                <Spin size='small' />
                <Text
                  variant='paragraphSmall'
                  weight='regular'
                  color={colors.NEUTRAL_50}
                  style='font-size: 14px; line-height: 1;'
                >
                  Loading More...
                </Text>
              </div>
            )}
            {!hasMore &&
              !loading &&
              items.length > 0 &&
              pagination &&
              pagination.total_pages > 1 && (
                <Text
                  variant='paragraphSmall'
                  weight='regular'
                  color={colors.NEUTRAL_50}
                  style='font-size: 14px;'
                >
                  No More Results
                </Text>
              )}
          </div>
        </>
      )}
      <FloatButton.BackTop
        icon={<ArrowUpOutlined />}
        css={css`
          .ant-float-btn-body {
            background-color: ${colors.PRIMARY} !important;
          }
          .ant-float-btn-icon {
            color: ${colors.NEUTRAL_0} !important;
          }
        `}
      />
    </div>
  )
}

const mapStateToProps = (state, { sportId }) => {
  return {
    gameTypes: getAllGameTypes(state),
    gameStatusTypes: getGameStatusTypesBySportId(state, sportId),
  }
}

export default connect(mapStateToProps)(GameList)
